import { useEffect, useState } from 'react';
import { useTechnologyService } from 'domain/shared/career';
import { usePositionService } from 'domain/shared/employee';
import type { PositionType } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import {
  type FilterBarFilterOption,
  type FilterBarFiltersProps,
  type FilterBarSearchInputProps,
} from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { POSITION_PROFILE_STATUS_LABELS } from '../constants';
import type { PositionProfilesFilters } from '../types';

type UsePositionProfilesFilterBarParams = {
  filters: PositionProfilesFilters;
  searchValue: string;
  setSearchValue: (value: string) => void;
  setFilters: (filters: PositionProfilesFilters) => void;
  resetFilters: () => void;
};

const usePositionProfilesFilterBar = ({
  filters,
  searchValue,
  setSearchValue,
  setFilters,
  resetFilters,
}: UsePositionProfilesFilterBarParams): {
  searchProps: FilterBarSearchInputProps;
  filterProps: FilterBarFiltersProps;
} => {
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const [positionOptions, setPositionOptions] = useState<FilterBarFilterOption[]>([]);
  const [technologyOptions, setTechnologyOptions] = useState<FilterBarFilterOption[]>([]);

  useEffect(() => {
    let isActive = true;

    Promise.all([findAllPositions(), findAllTechnologies()])
      .then(([positions, technologies]) => {
        if (!isActive) return;

        setPositionOptions(
          positions.map((position: PositionType) => ({ value: String(position.id), label: position.name })),
        );
        setTechnologyOptions(technologies.map(technology => ({ value: technology.id, label: technology.name })));
      })
      .catch(() => {
        if (isActive) {
          setPositionOptions([]);
          setTechnologyOptions([]);
        }
      });

    return () => {
      isActive = false;
    };
    // Dictionary services are recreated with their API hooks. The reference change is not a reload signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    searchProps: {
      label: 'Search',
      placeholder: 'Search',
      value: searchValue,
      onChange: setSearchValue,
    },
    filterProps: {
      value: filters,
      onChange: nextFilters => setFilters(nextFilters as PositionProfilesFilters),
      onReset: resetFilters,
      filters: [
        {
          id: 'specializationId',
          source: 'specializationId',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Position',
          multiple: true,
          options: positionOptions,
        },
        {
          id: 'technologyId',
          source: 'technologyId',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Technology',
          multiple: true,
          options: technologyOptions,
        },
        {
          id: 'competencyLevel',
          source: 'competencyLevel',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Level',
          multiple: true,
          options: Object.entries(EMPLOYEE_COMPETENCY_LEVEL).map(([value, label]) => ({ value, label })),
        },
        {
          id: 'status',
          source: 'status',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Status',
          multiple: true,
          options: Object.entries(POSITION_PROFILE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
        },
      ],
    },
  };
};

export default usePositionProfilesFilterBar;
